import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useModal } from 'lib/hooks';
import { Flex, Box, Hide, Text, StarRating, NakedButton } from 'roo-ui/components';
import { useDataLayer } from 'hooks/useDataLayer';
import Modal from 'components/Modal';
import ImageGallery from 'components/ImageGallery';
import TextButton from 'components/TextButton';
import RoomDescription from './RoomDescription';
import RoomTitle from './RoomTitle';
import RoomFacilities from './RoomFacilities';
import { CurrentImageText, LeftNav, RightNav } from './primitives';
import { getProperty, getTripAdvisorRating } from 'store/property/propertySelectors';
import TripAdvisorRating from 'components/TripAdvisorRating';
import TripAdvisorReviewsModal from 'components/TripAdvisorReviewsModal';

const RoomOfferDetails = ({ roomType }) => {
  const { name: roomName, maxOccupantCount, images, roomTypeFacilities, description } = roomType;
  const { name: propertyName, rating, ratingType, id } = useSelector(getProperty);
  const tripAdvisorRating = useSelector(getTripAdvisorRating);
  const { openModal, closeModal, modalProps } = useModal();
  const { openModal: openTripAdvisorModal, modalProps: tripAdvisorModalProps } = useModal();
  const { emitInteractionEvent } = useDataLayer();

  const openModalHandler = useCallback(() => {
    openModal();
    emitInteractionEvent({ type: 'View Room Details Pop Up', value: 'Link Selected' });
  }, [emitInteractionEvent, openModal]);

  const closeModalHandler = useCallback(() => {
    closeModal();
    emitInteractionEvent({ type: 'View Room Details Pop Up', value: 'Dismiss Selected' });
  }, [emitInteractionEvent, closeModal]);

  const handleTripAdvisorClick = useCallback(() => {
    openTripAdvisorModal();
    emitInteractionEvent({ type: 'Tripadvisor Pop Up', value: 'Top Link Selected' });
  }, [emitInteractionEvent, openTripAdvisorModal]);

  const handleOpenGallery = useCallback(() => {
    emitInteractionEvent({ type: 'Room Offer Details Gallery', value: 'Gallery Opened' });
  }, [emitInteractionEvent]);

  const handleGalleryImageChanged = useCallback(
    (id) => {
      emitInteractionEvent({ type: 'Room Offer Details Gallery', value: `Gallery Scrolled to ${id}` });
    },
    [emitInteractionEvent],
  );

  return (
    <Fragment>
      <TextButton onClick={openModalHandler} color="brand.primary" fontSize="base" aria-label={`View room details for ${roomName}`}>
        View room details
      </TextButton>
      <Modal
        {...modalProps}
        width="80%"
        padding={0}
        onRequestClose={closeModalHandler}
        title={
          <Flex>
            <Text fontSize={['base', 'md']} data-testid="modal-property-name">
              {propertyName}
            </Text>
            <Hide xs sm as={Flex} alignItems="center" ml={3}>
              <StarRating rating={rating} ratingType={ratingType} size={12} />
              {tripAdvisorRating && (
                <NakedButton onClick={handleTripAdvisorClick}>
                  <TripAdvisorRating ml={2} rating={tripAdvisorRating} displayReviews underlined small />
                </NakedButton>
              )}
            </Hide>
          </Flex>
        }
      >
        <Hide lg>
          <ImageGallery
            images={images}
            imageWidth="100%"
            imageHeight={['213px', '400px']}
            imageSize="medium"
            hideThumbnails
            imageDescriptionRenderer={CurrentImageText}
            leftNavRenderer={LeftNav}
            rightNavRenderer={RightNav}
            onClick={handleOpenGallery}
            onImageChanged={handleGalleryImageChanged}
          />
        </Hide>
        <Flex flexDirection={['column', 'column', 'row']} justifyContent="space-between" alignItems="flex-start">
          <Box p={[5, 5, 8]} pb={[0, 0, 8]} width={['100%', '100%', '40%']}>
            <RoomTitle name={roomName} maxOccupantCount={maxOccupantCount} />
            <RoomDescription description={description} />
          </Box>
          <Flex flexDirection="column" width={['100%', '100%', '60%']} borderLeft={1} borderColor="greys.alto">
            <Hide xs sm md>
              <ImageGallery
                images={images}
                imageWidth="100%"
                imageHeight="385px"
                imageSize="large"
                imageDescriptionRenderer={CurrentImageText}
                leftNavRenderer={LeftNav}
                rightNavRenderer={RightNav}
                onClick={handleOpenGallery}
                onImageChanged={handleGalleryImageChanged}
              />
            </Hide>
            <RoomFacilities facilities={roomTypeFacilities} />
          </Flex>
        </Flex>
      </Modal>
      <TripAdvisorReviewsModal {...tripAdvisorModalProps} locationId={id} />
    </Fragment>
  );
};

RoomOfferDetails.propTypes = {
  roomType: PropTypes.shape({
    name: PropTypes.string,
    maxOccupantCount: PropTypes.number,
    images: PropTypes.arrayOf(PropTypes.object),
    roomTypeFacilities: PropTypes.arrayOf(PropTypes.string),
    description: PropTypes.string,
  }).isRequired,
};

export default RoomOfferDetails;
